import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Footer from "@/components/Footer";

const STORY_SLIDES = [
  {
    src: "/assets/LHC.jpg",
    alt: "Large Hadron Collider at CERN",
    caption: "Large Hadron Collider, CERN",
  },
  {
    src: "/assets/jad-lhc.png", // <-- make sure this filename is REAL
    alt: "Dr Jad Marrouche at CERN",
    caption: "CERN",
  },
];


const coreServices = [
  { title: "Strategy & Consulting", desc: "Strategic advisory on AI adoption and business transformation." },
  { title: "Agentic AI Automation", desc: "Building intelligent AI agents to automate workflows." },
  { title: "Cloud & Data Integration", desc: "Unifying cloud platforms, legacy systems and AI data pipelines." },
  { title: "Cybersecurity & Risk Management", desc: "Securing operations through automation and governance." },
  { title: "IT Support & Maintenance", desc: "Fast, reliable troubleshooting and ongoing maintenance to minimise downtime." },
];


const leaders = [
  {
    name: "Dr. Jad Marrouche",
    title: "Founder & CEO",
    image: "/assets/jad-marrouche.png",
    bio: "Dr. Jad Marrouche is the Founder and CEO of COMPU-J. With 20 years of leadership experience spanning a decade as Chief Scientist at Matrix8J, where he developed systematic trading algorithms and his career as a particle physicist fellow at CERN, he brings a unique blend of strategic and technical expertise. Jad was the co-founder of Red Sun Fitness, a tech-focused fitness centre in collaboration with the UK’s National Health Service (NHS), to support individuals with disabilities in achieving sustained wellbeing. He holds a PhD in experimental particle physics from Imperial College London and was part of the CMS team at CERN that discovered the Nobel-prize winning Higgs Boson.",
  },
  {
    name: "Daniel Page",
    title: "Senior Advisor",
    image: "/assets/daniel-page-new.png",
    bio: "Daniel Page, is a senior corporate adviser with more than 25 years of corporate experience across tier-one institutions including Goldman Sachs, Deutsche Bank, and KPMG, where he focused on maximising commercial value. He later moved into the Family Office sector, spending seven years with the Bedrock Group as an investor and deal maker before co-founding the firm's technology platform business, BRT.\n\nAs Head of AM Advisory at KPMG, Dan built a standout track record of value extraction in mature, founder-owned SMEs, including successful exits in multiple MSP's. Today, he operates a select advisory portfolio focused on building elite commercial organisations and maximising shareholder returns. Dan is a British Citizen Award honouree, a Royal Charter holder with the Chartered Institute for Securities & Investment, and was awarded the Freedom of the City of London in 2014 by Lady Fiona Woolfe; he is also a clothed member of the Worshipful Company of Fruiterers, a champion of the social capital charity LTSB, and holds a professional Seconds licence with the British Boxing Board of Control.",
  },

];


function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".section-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function AboutPage() {
  const pageRef = useSectionReveal();
  const [leaderIdx, setLeaderIdx] = useState(0);

  const prevLeader = () => setLeaderIdx((p) => (p === 0 ? leaders.length - 1 : p - 1));
  const nextLeader = () => setLeaderIdx((p) => (p === leaders.length - 1 ? 0 : p + 1));

    const timelineRef = useRef(null);
  const lineRef = useRef(null);
  const dotRef = useRef(null);

    const [storyIdx, setStoryIdx] = useState(0);
  const [storyFade, setStoryFade] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) return;

    const intervalMs = 6500;

    const id = setInterval(() => {
      setStoryFade(true);
      setTimeout(() => {
        setStoryIdx((i) => (i + 1) % STORY_SLIDES.length);
        setStoryFade(false);
      }, 350);
    }, intervalMs);

    return () => clearInterval(id);
  }, []);

  // ✅ useEffect MUST be here (before return)
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) return;

    const section = timelineRef.current;
    const line = lineRef.current;
    const dot = dotRef.current;
    if (!section || !line || !dot) return;

    let raf = 0;

    const update = () => {
      raf = 0;

      const scrollY = window.scrollY;
      const top = section.offsetTop;
      const height = section.offsetHeight;

      const start = top - window.innerHeight * 0.5;
      const end = top + height - window.innerHeight * 0.5;

      const progress = Math.min(1, Math.max(0, (scrollY - start) / (end - start)));

      const lineH = line.offsetHeight;
      const dotH = dot.offsetHeight || 14;
      const y = progress * Math.max(0, lineH - dotH);

      dot.style.transform = `translate(-50%, ${y}px)`;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div ref={pageRef} style={{ backgroundColor: "#050505" }}>
      {/* Hero */}
<section
  className="pt-44 md:pt-52 pb-24 md:pb-36 px-6 md:px-12 lg:px-24 relative overflow-hidden"
  data-testid="about-hero"
>
 <img
  src="/assets/wave-form-new.svg"
  alt=""
  aria-hidden="true"
  className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-75"
/>

  {/* Content (kept above the background) */}
  <div className="relative z-10 max-w-5xl">
    <h1
      className="animate-hero-title text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
      data-testid="about-headline"
    >
      About Us
    </h1>

    <p
      className="animate-hero-sub text-base sm:text-lg lg:text-xl max-w-4xl leading-relaxed mb-6"
      style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
    >
      Compu-J is an IT services and AI consulting firm delivering secure, bespoke platforms for organisations globally, with deep experience in wealth management and automotive services. We turn complex problems into implemented systems—designed for real users, integrated with enterprise tools, and built with controls that support auditability, privacy, and long-term operation.
    </p>

    <p
      className="animate-hero-sub text-base sm:text-lg lg:text-xl max-w-4xl leading-relaxed"
      style={{ color: "#A1A1AA", fontFamily: "'Lora', serif", animationDelay: "0.4s" }}
    >
      From strategy to implementation, we deliver end-to-end technology and AI transformation that drives real operational
      efficiency, designed around your workflows, data, and governance controls. •	Maximising adoption and reducing operational risk is our core focus. We deliver through a modular engineering approach - secure data pipelines, robust system integrations, and production-grade monitoring, so solutions are reliable, maintainable, and ready for day-to-day use.
    </p>
  </div>
</section>

{/* Timeline: Future / Present / Vision (scroll reveal + one moving dot) */}
<section
  ref={timelineRef}
  className="pt-36 md:pt-44 pb-24 md:pb-32 px-6 md:px-12 lg:px-24 relative"
  data-testid="about-timeline"
>
  <div className="max-w-5xl mx-auto relative">
    {/* Present (right) */}
    <div className="section-reveal grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
      <div />
      <div>
        <p
          className="text-lg md:text-xl font-semibold mb-3"
          style={{ color: "#AF4AFA", fontFamily: "'Lora', serif" }}
        >
          Present
        </p>

        <p
          className="text-base md:text-lg leading-relaxed"
          style={{ color: "#F5F5F5", fontFamily: "'Lora', serif", opacity: 0.9 }}
        >
          Many firms still rely on fragmented CRMs, disconnected planning/reporting tools, and manual workflows across email
          and spreadsheets. The result: slow onboarding, duplicated work, inconsistent data, and avoidable compliance risk —
          while advisors lose time to administration instead of clients.
        </p>
      </div>
    </div>

    {/* Future (left) */}
    <div className="section-reveal grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
      <div>
        <p
          className="text-lg md:text-xl font-semibold mb-3"
          style={{ color: "#AF4AFA", fontFamily: "'Lora', serif" }}
        >
          Future
        </p>

        <p
          className="text-base md:text-lg leading-relaxed"
          style={{ color: "#F5F5F5", fontFamily: "'Lora', serif", opacity: 0.9 }}
        >
          AI will move wealth management from reactive processes to proactive, context-aware execution. Data will flow
          end-to-end, routine tasks will be automated, and insights will be delivered in real-time — backed by explainable
          rationale, approvals, and audit trails. AI won&apos;t replace advisors; it will remove operational drag and strengthen
          decision-making with governance built in.
        </p>
      </div>
      <div />
    </div>

    {/* OUR VISION (right) */}
    <div className="section-reveal grid grid-cols-1 md:grid-cols-2 gap-12">
      <div />
      <div>
        <p
          className="text-lg md:text-xl font-semibold mb-3"
          style={{ color: "#AF4AFA", fontFamily: "'Lora', serif" }}
        >
          Our Vision
        </p>

        <p
          className="text-base md:text-lg leading-relaxed"
          style={{ color: "#F5F5F5", fontFamily: "'Lora', serif", opacity: 0.9 }}
        >
          Compu.J helps wealth firms reach that standard. We deliver secure, enterprise-ready AI infrastructure that connects
          onboarding, client management, document workflows, insights, and compliance into one system. The outcome is faster
          execution, clearer oversight, and more time for teams to focus on advice and relationships.
        </p>
      </div>
    </div>

    {/* Center line + ONE moving dot (desktop only) */}
    <div ref={lineRef} aria-hidden="true" className="about-timeline-line hidden md:block">
      <span ref={dotRef} className="about-timeline-dot" />
    </div>
  </div>
</section>

{/* Our Story */}
<section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-story">
  <div className="section-reveal max-w-7xl mx-auto">
    <h2
      className="text-3xl md:text-5xl font-medium mb-4"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      Our Story
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: "#A1A1AA" }}>
          Compu.J was founded in 2011 by Dr Jad Marrouche during his tenure at CERN - one of the world's leading scientific
          research institutions.
        </p>

        <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: "#A1A1AA" }}>
          What began as a passion project - helping friends and family solve small technical challenges - quickly grew into
          something bigger. Driven by a love of problem-solving, Jad would often write code on flights between London and
          Geneva, turning ideas into practical solutions. That spirit of service and innovation remains at the core of Compu.J.
        </p>

        <p className="text-base md:text-lg leading-relaxed" style={{ color: "#A1A1AA" }}>
          Today, we partner with organisations across industries to deliver secure, high-impact technology - enabling transformation,
          improving efficiency, and unlocking new possibilities for our clients.
        </p>
      </div>

      <div className="relative">
  <div className="overflow-hidden" style={{ borderRadius: 26, backgroundColor: "black" }}>
    <img
      src={STORY_SLIDES[storyIdx].src}
      alt={STORY_SLIDES[storyIdx].alt}
      style={{ borderRadius: 26 }}
      className={
        "w-full h-64 md:h-80 object-cover transition-opacity duration-500 " +
        (storyFade ? "opacity-0" : "opacity-100")
      }
    />
  </div>

  <p
    className="text-sm md:text-base text-right mt-3"
    style={{ color: "#71717A", fontFamily: "'Lora', serif" }}
  >
          Large Hadron Collider, CERN 
        </p>
      </div>
      </div>
    </div>
</section>

      {/* Our Core Services */}
<section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-services">
  <div className="section-reveal max-w-7xl mx-auto">
    <h2
      className="text-3xl md:text-5xl font-medium mb-12"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
      data-testid="services-headline"
    >
      Our Core Services
    </h2>

    {/* Top row (3) */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {coreServices.slice(0, 3).map((s, i) => (
        <div
          key={s.title}
          className="rounded-2xl p-8 flex flex-col"
          style={{
            height: "260px",
            background: "linear-gradient(180deg, #0B0B10 0%, #1E1F23 100%)",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
          }}
          data-testid={`service-card-${i}`}
        >
          <div className="flex items-start justify-between">
            <h3
              className="service-title-gradient text-xl md:text-2xl font-medium leading-snug"
              style={{ fontFamily: "'Lora', serif" }}
            >
              {s.title}
            </h3>
          </div>

          <div className="flex-1" />

          <p className="text-base md:text-lg leading-relaxed" style={{ color: "#F5F5F5", opacity: 0.92 }}>
            {s.desc}
          </p>
        </div>
      ))}
    </div>

    {/* Bottom row (2 centered) */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 md:max-w-3xl md:mx-auto">
      {coreServices.slice(3).map((s, i) => (
        <div
          key={s.title}
          className="rounded-2xl p-8 flex flex-col"
          style={{
            height: "280px",
            background: "linear-gradient(180deg, #0B0B10 0%, #1E1F23 100%)",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
          }}
          data-testid={`service-card-${i + 3}`}
        >
          <div className="flex items-start justify-between">
            <h3
              className="service-title-gradient text-xl md:text-2xl font-medium leading-snug"
              style={{ fontFamily: "'Lora', serif" }}
            >
              {s.title}
            </h3>
          </div>

          <div className="flex-1" />

          <p className="text-base md:text-lg leading-relaxed" style={{ color: "#F5F5F5", opacity: 0.92 }}>
            {s.desc}
          </p>
        </div>
      ))}
    </div>
  </div>
</section>

      {/* Our Leaders */}
<section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-leaders">
  <div className="section-reveal max-w-7xl mx-auto">
    <h2
      className="text-3xl md:text-5xl font-medium mb-4"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      Our Leaders
    </h2>

    <p className="text-sm mb-12 max-w-3xl" style={{ color: "#A1A1AA" }}>
      We partner with individuals who embody excellence, integrity, and an entrepreneurial spirit. Our ambition is to build
      a firm that endures, not just by delivering technology, but by creating lasting impact for the organisations we serve.
    </p>

    <div className="relative max-w-4xl mx-auto">
      <div
  className="rounded-[28px] p-6 border border-white/10 overflow-hidden"
  style={{
    background:
      "radial-gradient(900px 520px at 18% 18%, rgba(99,115,250,0.22) 0%, rgba(175,74,250,0.10) 40%, transparent 72%), linear-gradient(180deg, rgba(17,18,27,0.92) 0%, rgba(10,10,15,0.92) 100%)",
    boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
  }}
>
  <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-7 items-start">
    {/* Photo */}
    <div
      className="rounded-[18px] overflow-hidden border border-white/10 h-[340px] md:h-[280px]"
      style={{ boxShadow: "0 14px 40px rgba(0,0,0,0.35)" }}
    >
     <img
  src={leaders[leaderIdx].image}
  alt={leaders[leaderIdx].name}
  className="w-full h-full object-cover grayscale"
  style={{
    objectPosition: leaders[leaderIdx].name === "Daniel Page" ? "40% 15%" : "50% 15%",
  }}
/>
    </div>

    {/* Copy */}
    <div className="pt-1">
      <h3 className="text-2xl md:text-3xl font-medium mb-1" style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}>
        {leaders[leaderIdx].name}
      </h3>

      <p className="text-sm md:text-base mb-4" style={{ color: "rgba(245,245,245,0.85)", fontFamily: "'Lora', serif" }}>
        {leaders[leaderIdx].title}
      </p>

      <p className="text-sm md:text-base leading-relaxed whitespace-pre-line" style={{ color: "rgba(245,245,245,0.70)" }}>
        {leaders[leaderIdx].bio}
      </p>
    </div>
  </div>
</div>

      <div className="flex items-center justify-center gap-4 mt-8">
        <button
          onClick={prevLeader}
          className="w-8 h-8 rounded-full glass flex items-center justify-center"
          aria-label="Previous leader"
        >
          <ChevronLeft size={16} style={{ color: "#A1A1AA" }} />
        </button>

        <div className="flex gap-2">
          {leaders.map((_, i) => (
            <button
              key={i}
              onClick={() => setLeaderIdx(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === leaderIdx ? "bg-[#6373FA] w-4" : "bg-white/20"}`}
              aria-label={`Go to leader ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextLeader}
          className="w-8 h-8 rounded-full glass flex items-center justify-center"
          aria-label="Next leader"
        >
          <ChevronRight size={16} style={{ color: "#A1A1AA" }} />
        </button>
      </div>
    </div>
  </div>
</section>

      <Footer />
    </div>
  );
}